import { createSelector } from 'reselect';

import { formSelector } from '@xcritical/forms';
import { getModalByName, IStoreWithModal } from '@xcritical/modal';

import { getUserRights, getUser, checkRight } from '@crm-framework/auth';

import { IStateWithLead } from '@crm-lead/core';
import { ICoreState } from '@crm-package/app';
import { getDictionaryByNameSelector } from '@crm-package/dictionaries';
import { USER_RIGHTS } from '@crm-package/auth';

import {
  LEAD_DETAILS_RIGHTS,
  LEAD_FORM_NAME,
  LeadDetailsDictionaries,
  STATUS_FULLY_ACTIVATED,
  STATUS_NEW,
  STATUS_NOT_VALID,
  STATUS_NO_POTENTIAL,
  departmentByDictionaryName,
  leadDetailsRightsType,
  CONTACT_DATA_FORM,
  CONTACT_DATA_MODAL,
} from './consts';
import { ILeadResponseModel } from './types';

export const leadFormSelector = <
  TModel extends ILeadResponseModel = ILeadResponseModel
>(
  state: IStateWithLead
) => formSelector<TModel>(state, LEAD_FORM_NAME);

export const contactDataFormSelector = (state: IStateWithLead) =>
  formSelector(state, CONTACT_DATA_FORM);

export const getStatusBlockValues = createSelector(
  leadFormSelector,
  (state: ICoreState) =>
    getDictionaryByNameSelector(state, LeadDetailsDictionaries.salesStatus),
  ({ model }, salesStatusDictionary) => {
    const salesStatusName =
      salesStatusDictionary.find((item) => item.id === model.salesStatusId)
        ?.name ?? STATUS_NEW;

    return {
      rejectReasonDictionaryName:
        STATUS_NOT_VALID === salesStatusName
          ? LeadDetailsDictionaries.eligibilityRejectReason
          : LeadDetailsDictionaries.salesStatusRejectReason,
      isDisabledTradingStatusField: salesStatusName !== STATUS_FULLY_ACTIVATED,
      isDisabledRejectReasonField: !(
        salesStatusName === STATUS_NO_POTENTIAL ||
        salesStatusName === STATUS_NOT_VALID
      ),
    };
  }
);

export const getCountryStateOptions = createSelector(
  leadFormSelector,
  (state: ICoreState) =>
    getDictionaryByNameSelector(state, LeadDetailsDictionaries.countryStates),
  ({ model }, states) =>
    states.find((countries) => countries.countryId === model.countryId)?.states
);

export const checkRightForEdit = createSelector(getUserRights, (userRights) =>
  Object.entries(LEAD_DETAILS_RIGHTS).reduce<
    Record<leadDetailsRightsType, boolean> | {}
  >((acc, [key, value]) => {
    acc[key] = value.some((item) => userRights.includes(item));

    return acc;
  }, {})
);

export const contactDataModalSelector = (state: IStoreWithModal) =>
  getModalByName(state, CONTACT_DATA_MODAL);

export const contactDataSelector = createSelector(
  contactDataModalSelector,
  contactDataFormSelector,
  ({ isOpen, fieldName = '' }, { isChanged }) => ({
    isOpen,
    fieldName,
    isChanged,
  })
);

export const canAssignableSelector = createSelector(
  (_: any, departament: string) => departament,
  getUser,
  (departament, user) => {
    const departments: string[] = user?.departments.map((item) =>
      item.toLowerCase()
    );

    return departments.includes(departmentByDictionaryName[departament]);
  }
);

export const checkAssignmentRights = createSelector(
  checkRightForEdit,
  (state: ICoreState) =>
    canAssignableSelector(state, LeadDetailsDictionaries.conversionAgent),
  (state: ICoreState) =>
    canAssignableSelector(state, LeadDetailsDictionaries.retentionManager),
  (state: ICoreState) =>
    canAssignableSelector(state, LeadDetailsDictionaries.vipManager),
  (state: ICoreState) =>
    canAssignableSelector(state, LeadDetailsDictionaries.closerManager),
  (
    checkingRights,
    canAssignConversionManager,
    canAssignRetentionManager,
    canAssignVipManager,
    canAssignCloserManager
  ) => {
    const canEditManagerRights =
      checkingRights[leadDetailsRightsType.editManagerRights];

    return {
      checkingRights,
      canAssignConversionManager:
        canAssignConversionManager || canEditManagerRights,
      canAssignRetentionManager:
        canAssignRetentionManager || canEditManagerRights,
      canAssignVipManager: canAssignVipManager || canEditManagerRights,
      canAssignCloserManager:
        canAssignCloserManager ||
        canEditManagerRights ||
        checkingRights[leadDetailsRightsType.editCloserManagerRights],
    };
  }
);

export const checkIsShownAgreementsRights = (state: IStateWithLead) =>
  checkRight(state, USER_RIGHTS.SEND_AGREEMENTS_ASSIGNED) ||
  checkRight(state, USER_RIGHTS.SEND_AGREEMENTS_ALL);
